import React, { useState, useEffect } from "react";
import { Tag, Plus, Loader2, Edit, Trash2 } from "lucide-react";
import { getStatuses, createStatus, updateStatus, deleteStatus } from "../apis/livechat";
import { toast } from "react-toastify";

/**
 * Chat Statuses - Admin-only page to create and manage chat statuses (name + color).
 * Section: agents (only admin has access).
 */
const ChatStatuses = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#6b7280", display_order: 0 });
  const [showForm, setShowForm] = useState(false);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const res = await getStatuses();
      if (res.data?.success && Array.isArray(res.data.data)) {
        setStatuses(res.data.data);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load statuses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatuses();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateStatus(editingId, {
          name: form.name.trim(),
          color: form.color || "#6b7280",
          display_order: form.display_order ?? 0,
        });
        toast.success("Status updated");
      } else {
        await createStatus({
          name: form.name.trim(),
          color: form.color || "#6b7280",
          display_order: form.display_order ?? 0,
        });
        toast.success("Status created");
      }
      setForm({ name: "", color: "#6b7280", display_order: 0 });
      setEditingId(null);
      setShowForm(false);
      loadStatuses();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to save status");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (s) => {
    setEditingId(s.id);
    setForm({ name: s.name, color: s.color || "#6b7280", display_order: s.display_order ?? 0 });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this status? Chats with this status will have status cleared.")) return;
    try {
      await deleteStatus(id);
      toast.success("Status deleted");
      loadStatuses();
      if (editingId === id) {
        setEditingId(null);
        setForm({ name: "", color: "#6b7280", display_order: 0 });
        setShowForm(false);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to delete status");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Tag size={24} className="text-green-600" />
            Chat Statuses
          </h1>
          <button
            type="button"
            onClick={() => {
              setEditingId(null);
              setForm({ name: "", color: "#6b7280", display_order: 0 });
              setShowForm(!showForm);
            }}
            className="ping-btn ping-btn-primary ping-btn-sm"
          >
            <Plus size={18} />
            Add status
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-4 mb-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">{editingId ? "Edit status" : "New status"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary"
                  placeholder="e.g. In Progress"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Color</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="h-10 w-12 rounded border border-gray-200 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={form.color}
                    onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Display order</label>
                <input
                  type="number"
                  min={0}
                  value={form.display_order}
                  onChange={(e) => setForm((f) => ({ ...f, display_order: parseInt(e.target.value, 10) || 0 }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                type="submit"
                disabled={saving}
                className="ping-btn ping-btn-primary ping-btn-sm disabled:opacity-50"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                {editingId ? "Update" : "Create"}
              </button>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); setForm({ name: "", color: "#6b7280", display_order: 0 }); }}
                className="ping-btn ping-btn-ghost ping-btn-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : statuses.length === 0 ? (
            <div className="py-12 text-center text-gray-500 text-sm">
              No statuses yet. Add one to use in Live Chats.
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {statuses.map((s) => (
                <li key={s.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <span
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: s.color || "#9ca3af" }}
                    />
                    <span className="font-medium text-gray-900">{s.name}</span>
                    <span className="text-xs text-gray-500">Order: {s.display_order ?? 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleEdit(s)}
                      className="p-2 text-gray-500 hover:text-primary hover:bg-primary/10 rounded-lg"
                      title="Edit"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(s.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatStatuses;
